import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, BarChart3, Users, Brain, FileText, TrendingUp, Loader2, RefreshCw } from 'lucide-react';
import AdminLayout from './AdminLayout';
import ExecutiveTab from '../../components/intelligence/ExecutiveTab';
import TeamTab from '../../components/intelligence/TeamTab';
import IndividualTab from '../../components/intelligence/IndividualTab';
import CoachingTab from '../../components/intelligence/CoachingTab';
import ReportsTab from '../../components/intelligence/ReportsTab';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'executive', label: 'Executive', icon: TrendingUp },
  { id: 'team', label: 'Team', icon: Users },
  { id: 'individual', label: 'Individual', icon: BarChart3 },
  { id: 'coaching', label: 'Coaching Queue', icon: Brain },
  { id: 'reports', label: 'Reports', icon: FileText },
];

const AdminDomainPortalPage = () => {
  const { domainId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [domain, setDomain] = useState(null);
  const [activeTab, setActiveTab] = useState('executive');
  const [gradingPending, setGradingPending] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const getHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
  }), [token]);

  const loadDomainInfo = useCallback(async () => {
    const headers = getHeaders();
    const [dRes, callsRes] = await Promise.allSettled([
      axios.get(`${API}/admin/domains/${domainId}`, { headers }),
      axios.get(`${API}/calls?domain_id=${domainId}&status=pending&limit=200`, { headers }),
    ]);
    if (dRes.status === 'fulfilled') setDomain(dRes.value.data);
    if (callsRes.status === 'fulfilled') setPendingCount(callsRes.value.data.length);
  }, [domainId, getHeaders]);

  useEffect(() => { loadDomainInfo(); }, [loadDomainInfo]);

  const handleGradePending = async () => {
    if (!window.confirm(`AI-grade ${pendingCount} pending calls? This will fetch transcripts and run AI grading. May take a few minutes.`)) return;
    setGradingPending(true);
    try {
      const r = await axios.post(`${API}/admin/domains/${domainId}/grade-pending`, {}, { headers: getHeaders() });
      if (r.data.pending === 0) {
        toast.info(r.data.message);
      } else {
        toast.success(`${r.data.message} Refresh in ~2 minutes to see updated scores.`);
        setPendingCount(0);
      }
    } catch {
      toast.error('Failed to start grading');
    } finally {
      setGradingPending(false);
    }
  };

  const companyName = domain?.company?.name || domain?.yabbit_domain || '...';

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/admin/domains/${domainId}`)}
          className="flex items-center gap-2 text-white/40 hover:text-white text-sm mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {companyName}
        </button>

        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">{companyName}</h1>
            <p className="text-white/40 text-sm mt-0.5">
              Portal Dashboard · {domain?.yabbit_domain} · {domain?.industry?.replace(/_/g, ' ')}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {pendingCount > 0 && (
              <button
                onClick={handleGradePending}
                disabled={gradingPending}
                className="flex items-center gap-2 px-3 py-2 bg-amber-500 hover:bg-amber-400 text-white text-sm font-medium rounded-lg transition-colors"
                data-testid="grade-pending-portal-btn"
              >
                {gradingPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
                Grade {pendingCount} Pending
              </button>
            )}
            <button
              onClick={loadDomainInfo}
              className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 text-sm rounded-lg transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
            {/* Tab bar */}
            <div className="flex gap-1 bg-white/5 p-1 rounded-xl flex-wrap">
              {TABS.map(t => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id)}
                    data-testid={`portal-tab-${t.id}`}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      activeTab === t.id ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/70'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Tab content in a light wrapper to match existing tab styles */}
      <div className="bg-white rounded-2xl shadow-sm p-6 min-h-[500px]">
        {activeTab === 'executive' && <ExecutiveTab domainId={domainId} />}
        {activeTab === 'team' && <TeamTab domainId={domainId} />}
        {activeTab === 'individual' && <IndividualTab domainId={domainId} />}
        {activeTab === 'coaching' && (
          <CoachingTab
            domainId={domainId}
            onAgentClick={(agentId) => navigate(`/admin/domains/${domainId}?tab=calls&agent_id=${agentId}`)}
          />
        )}
        {activeTab === 'reports' && <ReportsTab domainId={domainId} />}
      </div>
    </AdminLayout>
  );
};

export default AdminDomainPortalPage;
